import {
  CompRef,
  DocumentServicesObject,
  DsItem,
} from '@wix/document-services-types';
import {
  WidgetPointer,
  WidgetSlot,
  WidgetSlotPosition,
} from '@wix/editor-platform-host-integration-apis';
import {
  getPluginInfo,
  createWidgetPluginComponentDefinition,
  virtualSlotPlaceholderSeparator,
  buildComponentPreviewState,
  getEditorPreviewFrame,
  getContainer_DEPRECATED_BAD_PERFORMANCE,
} from '../utils/pluginUtils';
import {
  ComponentPreviewState,
  SUPPORTED_HOST_PLUGIN_COMPONENT_TYPES,
} from '../constants';
import { PluginHostWidgetApi } from '../types';

export interface OOIWidgetSlot {
  displayName: string;
  name: string;
}

function getVirtualSlotPlaceholderData(compRef: CompRef): {
  widgetHostRef: CompRef;
  slotName: string;
} {
  const [hostId, slotName] = compRef.id.split(virtualSlotPlaceholderSeparator);

  return {
    widgetHostRef: {
      id: hostId,
      type: compRef.type,
    },
    slotName,
  };
}

function getSlotsFromAppManifest(
  documentServicesAPI: DocumentServicesObject,
  widgetCompRef: CompRef,
): OOIWidgetSlot[] {
  const { appDefinitionId, widgetId } =
    documentServicesAPI.components.data.get(widgetCompRef);

  if (!appDefinitionId) {
    return [];
  }

  const appManifest =
    documentServicesAPI.platform.getAppManifest(appDefinitionId);

  if (!appManifest) {
    return [];
  }

  const slotsMap: Record<string, Pick<OOIWidgetSlot, 'displayName'>> =
    // @ts-expect-error
    appManifest.controllersStageData?.[widgetId]?.default?.slots ?? {};

  return Object.entries(slotsMap).map(([slotName, slotConfig]) => ({
    name: slotName,
    ...slotConfig,
  }));
}

function getVirtualSlotsPlaceholderCompRef(
  widgetCompRef: CompRef,
  slotName: string,
): CompRef {
  return {
    id: `${widgetCompRef.id}${virtualSlotPlaceholderSeparator}${slotName}`,
    type: widgetCompRef.type,
  };
}

function getWidgetSlotData(
  documentServicesAPI: DocumentServicesObject,
  hostRef: CompRef,
  slot: OOIWidgetSlot,
): WidgetSlot {
  const slotId = slot.name;
  const { widgetId, appDefinitionId } =
    documentServicesAPI.components.data.get(hostRef);

  const slotData =
    documentServicesAPI.components.slots.getSlotsData(hostRef)?.[slotId];
  const slotComponentData: DsItem | undefined =
    documentServicesAPI.components.data.get(slotData!);
  return {
    compRef: getVirtualSlotsPlaceholderCompRef(hostRef, slotId),
    role: slotId,
    pluginInfo: getPluginInfo(documentServicesAPI, slotComponentData),
    placement: {
      appDefinitionId,
      widgetId,
      slotId,
    },
  };
}

export const tpaHostAPI: PluginHostWidgetApi = {
  getSlotPlaceholderDisplayName(
    documentServicesAPI: DocumentServicesObject,
    virtualSlotPlaceholderRef: CompRef,
  ): string | undefined {
    const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
      virtualSlotPlaceholderRef,
    );
    const slotsPlaceholders = getSlotsFromAppManifest(
      documentServicesAPI,
      widgetHostRef,
    );
    const slotPlaceholder = slotsPlaceholders.find((s) => s.name === slotName);
    return slotPlaceholder?.displayName;
  },
  getWidgetSlot(
    documentServicesAPI: DocumentServicesObject,
    virtualSlotsPlaceholderRef: CompRef,
  ): WidgetSlot {
    const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
      virtualSlotsPlaceholderRef,
    );
    const slot = { displayName: slotName, name: slotName };
    return getWidgetSlotData(documentServicesAPI, widgetHostRef, slot);
  },
  getWidgetSlots(
    documentServicesAPI: DocumentServicesObject,
    widgetCompRef: CompRef,
  ): WidgetSlot[] {
    const slots = getSlotsFromAppManifest(documentServicesAPI, widgetCompRef);
    return slots.map((slot) =>
      getWidgetSlotData(documentServicesAPI, widgetCompRef, slot),
    );
  },
  getSlotPosition(
    _: unknown,
    virtualSlotPlaceholderRef: CompRef,
  ): WidgetSlotPosition | null {
    const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
      virtualSlotPlaceholderRef,
    );

    const virtualSlotPlaceholderID = `${widgetHostRef.id}.${slotName}`;
    const previewFrame = getEditorPreviewFrame();
    const virtualSlotPlaceholderElement =
      previewFrame.contentWindow?.document.getElementById(
        virtualSlotPlaceholderID,
      );

    if (!virtualSlotPlaceholderElement) {
      return null;
    }

    const rect = virtualSlotPlaceholderElement.getBoundingClientRect();
    return {
      y: rect.y,
      height: rect.height,
    };
  },
  getSlotContentRef(
    documentServicesAPI: DocumentServicesObject,
    virtualSlotPlaceholderRef: CompRef,
  ): CompRef | undefined {
    const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
      virtualSlotPlaceholderRef,
    );
    return documentServicesAPI.components.slots.getSlotsData(widgetHostRef)[
      slotName
    ];
  },
  getCompRefForSlotSelection(
    documentServicesAPI: DocumentServicesObject,
    virtualSlotPlaceholderRef: CompRef,
  ): CompRef | undefined {
    return this.getSlotContentRef(
      documentServicesAPI,
      virtualSlotPlaceholderRef,
    );
  },
  isWidgetSlotPopulated(
    documentServicesAPI: DocumentServicesObject,
    virtualSlotPlaceholderRef: CompRef,
  ): boolean {
    return Boolean(
      this.getSlotContentRef(documentServicesAPI, virtualSlotPlaceholderRef),
    );
  },
  async populateWidgetPluginToSlot(
    documentServicesAPI: DocumentServicesObject,
    virtualSlotPlaceholderRef: CompRef,
    widgetPointer: WidgetPointer,
  ): Promise<CompRef> {
    const appData = documentServicesAPI.tpa.app.getDataByAppDefId(
      widgetPointer.appDefinitionId,
    );
    const widgetComponent = await createWidgetPluginComponentDefinition(
      documentServicesAPI,
      widgetPointer,
      appData.applicationId!,
    );

    const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
      virtualSlotPlaceholderRef,
    );

    const compRef: CompRef = documentServicesAPI.components.slots.populate(
      widgetHostRef,
      slotName,
      widgetComponent,
    );

    return compRef;
  },
  getWidgetHostRef(_: unknown, virtualSlotPlaceholderRef: CompRef): CompRef {
    const { widgetHostRef } = getVirtualSlotPlaceholderData(
      virtualSlotPlaceholderRef,
    );

    return widgetHostRef;
  },
  uninstallWidgetPlugin(
    documentServicesAPI: DocumentServicesObject,
    virtualSlotPlaceholderRef: CompRef,
  ) {
    const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
      virtualSlotPlaceholderRef,
    );

    documentServicesAPI.components.slots.remove(widgetHostRef, slotName);
  },
  getWidgetPointer(
    documentServicesAPI: DocumentServicesObject,
    virtualSlotPlaceholderRef: CompRef,
  ): WidgetPointer | undefined {
    const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
      virtualSlotPlaceholderRef,
    );

    const contentRef =
      documentServicesAPI.components.slots.getSlotsData(widgetHostRef)[
        slotName
      ];

    if (!contentRef) {
      return undefined;
    }

    const { appDefinitionId, widgetId } =
      documentServicesAPI.components.data.get(contentRef);

    return { appDefinitionId, widgetId };
  },
  getSlotPlaceholderRefByContentRef(
    documentServicesAPI: DocumentServicesObject,
    widgetPluginCompRef: CompRef,
  ): CompRef | undefined {
    const hostWidgetRef = getContainer_DEPRECATED_BAD_PERFORMANCE(
      documentServicesAPI,
      widgetPluginCompRef,
    );

    if (!hostWidgetRef) {
      return undefined;
    }

    const hostSlots =
      documentServicesAPI.components.slots.getSlotsData(hostWidgetRef);
    const [pluginSlotName] =
      Object.entries(hostSlots).find(
        ([, slotContentRef]) => slotContentRef?.id === widgetPluginCompRef.id,
      ) ?? [];

    return pluginSlotName
      ? getVirtualSlotsPlaceholderCompRef(hostWidgetRef, pluginSlotName)
      : undefined;
  },
  hasWidgetSlot(
    documentServicesAPI: DocumentServicesObject,
    widgetCompRef: CompRef,
  ): boolean {
    return (
      getSlotsFromAppManifest(documentServicesAPI, widgetCompRef).length > 0
    );
  },
  async highlightSlot(
    documentServicesAPI: DocumentServicesObject,
    virtualSlotPlaceholderRef: CompRef,
  ) {
    const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
      virtualSlotPlaceholderRef,
    );
    documentServicesAPI.documentMode.setComponentPreviewState(
      widgetHostRef.id,
      buildComponentPreviewState(slotName, ComponentPreviewState.HIGHLIGHTED),
    );
    await documentServicesAPI.waitForChangesAppliedAsync();
  },
  unhighlightSlot(
    documentServicesAPI: DocumentServicesObject,
    virtualSlotPlaceholderRef: CompRef,
  ) {
    const { widgetHostRef } = getVirtualSlotPlaceholderData(
      virtualSlotPlaceholderRef,
    );
    documentServicesAPI.documentMode.setComponentPreviewState(
      widgetHostRef.id,
      undefined,
    );
  },
  isPluginComponent(
    documentServicesAPI: DocumentServicesObject,
    compRef: CompRef,
  ): boolean {
    const parentRef = getContainer_DEPRECATED_BAD_PERFORMANCE(
      documentServicesAPI,
      compRef,
    );

    if (!parentRef) {
      return false;
    }

    // for OOI widgets widget plugins are hosted directly by TPA Widget
    const compType = documentServicesAPI.components.getType(parentRef);
    return SUPPORTED_HOST_PLUGIN_COMPONENT_TYPES.includes(compType);
  },
};
